import type { ClinicalRowReviewStatus, ClinicalSubmissionStatus } from '@starter/shared';

export function submissionStatusLabel(status: ClinicalSubmissionStatus): string {
  switch (status) {
    case 'DRAFT':
      return 'Draft';
    case 'PROCESSING':
      return 'Processing';
    case 'STUDENT_REVIEW_REQUIRED':
      return 'Needs My Review';
    case 'READY_FOR_INSTRUCTOR':
      return 'Pending Faculty';
    case 'UNDER_INSTRUCTOR_REVIEW':
      return 'Faculty Review';
    case 'CLARIFICATION_REQUESTED':
      return 'Returned';
    case 'APPROVED':
      return 'Approved';
    case 'REJECTED':
      return 'Rejected';
    case 'PROCESSING_FAILED':
      return 'Processing Failed';
    default:
      return status;
  }
}

export function submissionStatusVariant(
  status: ClinicalSubmissionStatus,
): 'default' | 'success' | 'warning' | 'muted' {
  switch (status) {
    case 'APPROVED':
      return 'success';
    case 'STUDENT_REVIEW_REQUIRED':
    case 'CLARIFICATION_REQUESTED':
      return 'warning';
    case 'READY_FOR_INSTRUCTOR':
    case 'UNDER_INSTRUCTOR_REVIEW':
      return 'default';
    default:
      return 'muted';
  }
}

export function occupancyLabel(state: string): string {
  switch (state) {
    case 'EMPTY':
      return 'Blank Row';
    case 'OCCUPIED':
      return 'Case Recorded';
    case 'UNCERTAIN':
      return 'Needs Review';
    default:
      return state.replace(/_/g, ' ');
  }
}

export function sourceCrossOutLabel(state: string | undefined): string {
  switch (state) {
    case 'PRESENT':
      return 'Crossed Out';
    case 'ABSENT':
      return 'Not Crossed Out';
    case 'UNCERTAIN':
      return 'Needs Review';
    default:
      return 'Needs Review';
  }
}

export function studentCrossOutResolutionLabel(resolution: string | null | undefined): string {
  if (!resolution) return 'Unresolved';
  switch (resolution) {
    case 'PRESENT':
      return 'Crossed Out';
    case 'ABSENT':
      return 'Not Crossed Out';
    default:
      return 'Unresolved';
  }
}

export function isCrossOutApproveBlocked(row: {
  sourceCrossedOutState?: string;
  studentCrossedOutResolution?: string | null;
}): boolean {
  if (row.sourceCrossedOutState === 'PRESENT') return true;
  if (row.studentCrossedOutResolution === 'PRESENT') return true;
  if (row.sourceCrossedOutState === 'UNCERTAIN' && !row.studentCrossedOutResolution) return true;
  return false;
}

export function signatureLabel(state: string): string {
  switch (state) {
    case 'PRESENT':
      return 'Signature Present';
    case 'MISSING':
      return 'Signature Missing';
    case 'UNCERTAIN':
      return 'Signature Unsure';
    default:
      return state.replace(/_/g, ' ');
  }
}

export function rowReviewStatusLabel(status: ClinicalRowReviewStatus): string {
  switch (status) {
    case 'AUTO_ACCEPT':
      return 'Auto-accepted';
    case 'STUDENT_REVIEW_REQUIRED':
      return 'Needs student review';
    case 'INSTRUCTOR_REVIEW_REQUIRED':
      return 'Needs instructor review';
    default:
      return humanizeCode(String(status));
  }
}

export function rowReviewStatusVariant(
  status: ClinicalRowReviewStatus,
): 'default' | 'success' | 'warning' | 'muted' {
  switch (status) {
    case 'AUTO_ACCEPT':
      return 'success';
    case 'STUDENT_REVIEW_REQUIRED':
      return 'warning';
    case 'INSTRUCTOR_REVIEW_REQUIRED':
      return 'default';
    default:
      return 'muted';
  }
}

export function rowReviewStatusMarker(status: ClinicalRowReviewStatus): string {
  switch (status) {
    case 'AUTO_ACCEPT':
      return 'AUTO';
    case 'STUDENT_REVIEW_REQUIRED':
      return 'STUDENT';
    case 'INSTRUCTOR_REVIEW_REQUIRED':
      return 'INSTRUCTOR';
    default:
      return humanizeCode(String(status)).slice(0, 12).toUpperCase();
  }
}

function humanizeCode(code: string): string {
  return code
    .trim()
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const REVIEW_REASON_LABELS: Record<string, string> = {
  PROCEDURE_MISSING: 'Procedure name missing',
  SPECIALTY_UNRESOLVED: 'Specialty could not be matched',
  SPECIALTY_MISSING: 'Specialty missing',
  SPECIALTY_AMBIGUOUS: 'Specialty is ambiguous',
  DATE_INVALID: 'Case date is not a valid date',
  DATE_MISSING: 'Case date missing',
  DATE_OUT_OF_RANGE: 'Case date outside the programme date range',
  ROLE_UNRESOLVED: 'Role could not be matched',
  ROLE_MISSING: 'Role missing',
  ROLE_AMBIGUOUS: 'Role is ambiguous',
  DUPLICATE_CONFLICT: 'Possible duplicate of another case',
  SOURCE_DOCUMENT_MISSING: 'Source document missing',
  CONFIDENCE_UNKNOWN: 'Confidence could not be determined',
  MODERATE_CONFIDENCE: 'Moderate confidence — please confirm',
  LOW_CONFIDENCE: 'Low confidence — please confirm',
};

export function reviewReasonLabel(code: string): string {
  const trimmed = code.trim();
  if (!trimmed) return humanizeCode('UNKNOWN');
  return REVIEW_REASON_LABELS[trimmed] ?? humanizeCode(trimmed);
}

export function confidenceLabel(confidence: number | null): string {
  if (confidence === null || confidence === undefined) return 'Unknown';
  return `${confidence}%`;
}

export function countRowReviewStatuses(
  rows: ReadonlyArray<{ reviewStatus: ClinicalRowReviewStatus }>,
): { autoAccept: number; studentReview: number; instructorReview: number } {
  let autoAccept = 0;
  let studentReview = 0;
  let instructorReview = 0;
  for (const row of rows) {
    switch (row.reviewStatus) {
      case 'AUTO_ACCEPT':
        autoAccept += 1;
        break;
      case 'STUDENT_REVIEW_REQUIRED':
        studentReview += 1;
        break;
      case 'INSTRUCTOR_REVIEW_REQUIRED':
        instructorReview += 1;
        break;
      default:
        break;
    }
  }
  return { autoAccept, studentReview, instructorReview };
}

export function caseRoleLabel(role: string | null | undefined): string {
  if (!role) return '—';
  switch (role) {
    case 'FIRST_SCRUB':
      return 'First Scrub';
    case 'SECOND_SCRUB':
      return 'Second Scrub';
    case 'OBSERVATION':
      return 'Observation';
    default:
      return humanizeCode(role);
  }
}

export function studentRowNeedsAction(row: {
  reviewStatus: ClinicalRowReviewStatus;
  reviewRequired: boolean;
  studentConfirmedAt?: string | null;
}): boolean {
  return row.reviewRequired;
}

export function rowFlagLabels(row: {
  reviewRequired: boolean;
  sourceProvenance?: string;
  sourceOccupancyState: string;
  sourceSignaturePresence: string;
  sourceCrossedOutObserved: boolean;
  sourceCrossedOutState?: string;
  studentCrossedOutResolution?: string | null;
  studentOccupancyResolution?: string | null;
  studentSignatureResolution?: string | null;
  procedureTextNormalized: string | null;
  automatedProcedureNormalized?: string | null;
  studentConfirmedAt?: string | null;
}): string[] {
  const flags: string[] = [];
  if (
    (row.sourceProvenance === 'AUTOMATED' || row.sourceProvenance === 'AUTOMATED_STUDENT_CONFIRMED') &&
    !row.studentConfirmedAt &&
    (row.automatedProcedureNormalized?.trim() || row.procedureTextNormalized?.trim())
  ) {
    flags.push('Suggested');
  }
  if (row.reviewRequired) flags.push('Needs Review');
  if (row.sourceOccupancyState === 'UNCERTAIN' && !row.studentOccupancyResolution) {
    flags.push('Needs Review');
  }
  if (row.sourceSignaturePresence === 'MISSING' || row.sourceSignaturePresence === 'UNCERTAIN') {
    flags.push('Signature Unsure');
  }
  if (row.sourceCrossedOutState === 'PRESENT') flags.push('Crossed Out');
  if (row.sourceCrossedOutState === 'UNCERTAIN' && !row.studentCrossedOutResolution) {
    flags.push('Cross-out Needs Review');
  }
  if (row.sourceOccupancyState === 'EMPTY' && !row.procedureTextNormalized?.trim()) {
    flags.push('Blank Row');
  }
  if (!row.procedureTextNormalized?.trim() && row.sourceOccupancyState !== 'EMPTY') {
    flags.push('Procedure Missing');
  }
  if (row.studentOccupancyResolution === 'EMPTY' && row.sourceOccupancyState !== 'EMPTY') {
    flags.push('Student Claims Blank');
  }
  return flags;
}
